import { Form, FormInstance, Input, Select, Spin } from "antd";
import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import {
  useGetMasterDistrict,
  useGetMasterProvince,
  useGetMasterSubDistrict,
} from "hooks/useMasterData";
import type {
  MasterDistrictData,
  MasterProvinceData,
  MasterSubDistrictData,
} from "services/interface";
import {
  PARCEL_DESTINATION_ADDRESS_MAX_LENGTH,
  ParcelSearchAgainBox,
} from "../../manageParcelLayout";
import {
  PARCEL_PHONE_MAX_DIGITS,
  PHONE_MISMATCH_SEARCH_LINE1,
  PHONE_MISMATCH_SEARCH_LINE2,
  sanitizeParcelPhone,
} from "./parcelPhoneSearchUtils";
import { Button, Typography } from "antd";

const { Option } = Select;

type ParcelCreateAddressFormProps = {
  form: FormInstance;
  phoneReadonly?: string;
  phoneEditable?: boolean;
  /** เบอร์ไม่ตรงผลค้นหา — ปิดฟิลด์อื่น (เว้นเบอร์ถ้าแก้ได้) */
  lockNonPhoneFields?: boolean;
  /** แสดงข้อความ + ปุ่มค้นหาใต้ช่องเบอร์ */
  showPhoneMismatchSearch?: boolean;
  onPhoneMismatchSearch?: () => void;
};

/**
 * ฟอร์มสร้างที่อยู่จัดส่ง — อ้างอิง CreateAgentAddressForm (post_office / taxi)
 * ไม่มีประเภทขนส่ง
 */
const ParcelCreateAddressForm: FC<ParcelCreateAddressFormProps> = ({
  form,
  phoneReadonly = "",
  phoneEditable = false,
  lockNonPhoneFields = false,
  showPhoneMismatchSearch = false,
  onPhoneMismatchSearch,
}) => {
  const [provinceId, setProvinceId] = useState<number>(0);
  const [districtId, setDistrictId] = useState<number>(0);
  const [provinces, setProvinces] = useState<MasterProvinceData[]>([]);
  const [districts, setDistricts] = useState<MasterDistrictData[]>([]);
  const [subDistricts, setSubDistricts] = useState<MasterSubDistrictData[]>([]);

  const { data: masterProvinceData, isLoading: provinceLoading } = useGetMasterProvince();
  const { data: masterDistrictData, isLoading: districtLoading } =
    useGetMasterDistrict(provinceId);
  const { data: masterSubDistrictData, isLoading: subDistrictLoading } =
    useGetMasterSubDistrict(districtId);

  useEffect(() => {
    if (masterProvinceData) setProvinces(masterProvinceData);
  }, [masterProvinceData]);

  useEffect(() => {
    if (masterDistrictData) setDistricts(masterDistrictData);
    else setDistricts([]);
  }, [masterDistrictData]);

  useEffect(() => {
    if (masterSubDistrictData) setSubDistricts(masterSubDistrictData);
    else setSubDistricts([]);
  }, [masterSubDistrictData]);

  useEffect(() => {
    if (phoneReadonly) {
      form.setFieldsValue({ address_telephone: phoneReadonly });
    }
  }, [phoneReadonly, form]);

  const validatePhoneComplete = (_: unknown, value: string) => {
    const digits = sanitizeParcelPhone(value);
    if (!digits) {
      return Promise.resolve();
    }
    if (digits.length < PARCEL_PHONE_MAX_DIGITS) {
      return Promise.reject(
        `กรุณากรอกเบอร์โทรศัพท์ให้ครบ ${PARCEL_PHONE_MAX_DIGITS} หลัก`,
      );
    }
    return Promise.resolve();
  };

  return (
    <FormWrapper>
      <FormItems
        labelAlign="left"
        label="ชื่อผู้รับสินค้า"
        name="address_name"
        rules={[{ required: true, message: "กรุณากรอกชื่อผู้รับสินค้า" }]}
      >
        <Input
          placeholder="ชื่อผู้รับสินค้า"
          size="small"
          autoComplete="off"
          disabled={lockNonPhoneFields}
        />
      </FormItems>

      <FormItems
        labelAlign="left"
        label="โทรศัพท์มือถือ"
        name="address_telephone"
        getValueFromEvent={(e) => sanitizeParcelPhone(e?.target?.value ?? "")}
        rules={[
          { required: true, message: "กรุณากรอกเบอร์โทรศัพท์มือถือ" },
          { validator: validatePhoneComplete },
        ]}
      >
        <Input
          placeholder="โทรศัพท์มือถือ สำหรับติดต่อผู้รับ"
          size="small"
          autoComplete="off"
          inputMode="numeric"
          maxLength={PARCEL_PHONE_MAX_DIGITS}
          readOnly={!phoneEditable && lockNonPhoneFields}
          disabled={lockNonPhoneFields && !phoneEditable}
        />
      </FormItems>

      {showPhoneMismatchSearch ? (
        <PhoneMismatchRow>
          <ParcelSearchAgainBox>
            <Typography.Text style={{ display: "block" }}>
              {PHONE_MISMATCH_SEARCH_LINE1}
            </Typography.Text>
            <Typography.Text style={{ display: "block" }}>
              {PHONE_MISMATCH_SEARCH_LINE2}
            </Typography.Text>
          </ParcelSearchAgainBox>
          <Button type="primary" size="small" onClick={() => onPhoneMismatchSearch?.()}>
            เพิ่ม/ค้นหา
          </Button>
        </PhoneMismatchRow>
      ) : null}

      <FormItems
        labelAlign="left"
        label="บ้านเลขที่/ซอย/ถนน"
        name="line_1"
        rules={[
          { required: true, message: "กรุณากรอกบ้านเลขที่/ซอย/ถนน" },
          {
            max: PARCEL_DESTINATION_ADDRESS_MAX_LENGTH,
            message: `ที่อยู่ต้องไม่เกิน ${PARCEL_DESTINATION_ADDRESS_MAX_LENGTH} ตัวอักษร`,
          },
        ]}
      >
        <Input
          placeholder="บ้านเลขที่/ซอย/ถนน"
          size="small"
          autoComplete="off"
          maxLength={PARCEL_DESTINATION_ADDRESS_MAX_LENGTH}
          disabled={lockNonPhoneFields}
        />
      </FormItems>

      <FormItems
        labelAlign="left"
        label="ปลายทางจังหวัด"
        name="province_id_ship"
        rules={[{ required: true, message: "กรุณากรอกปลายทางจังหวัด" }]}
      >
        <Select
          showSearch
          placeholder="จังหวัด"
          optionFilterProp="children"
          loading={provinceLoading}
          disabled={lockNonPhoneFields}
          notFoundContent={provinceLoading ? <Spin size="small" /> : null}
          onChange={(value: number) => {
            setProvinceId(value);
            form.setFieldsValue({
              district_id_ship: undefined,
              sub_district_id_ship: undefined,
              zip_code_ship: undefined,
            });
          }}
        >
          {provinces.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name_th}
            </Option>
          ))}
        </Select>
      </FormItems>

      <FormItems
        label="ปลายทางเขต/อำเภอ"
        labelAlign="left"
        name="district_id_ship"
        rules={[{ required: true, message: "กรุณาเลือกปลายทางเขต/อำเภอ" }]}
      >
        <Select
          showSearch
          placeholder="ปลายทางเขต/อำเภอ"
          optionFilterProp="children"
          disabled={lockNonPhoneFields || !provinceId}
          loading={districtLoading}
          notFoundContent={districtLoading ? <Spin size="small" /> : null}
          onChange={(value: number) => {
            setDistrictId(value);
            form.setFieldsValue({
              sub_district_id_ship: undefined,
              zip_code_ship: undefined,
            });
          }}
        >
          {districts.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name_th}
            </Option>
          ))}
        </Select>
      </FormItems>

      <FormItems
        label="ปลายแขวง/ตำบล"
        labelAlign="left"
        name="sub_district_id_ship"
        rules={[{ required: true, message: "กรุณาเลือกปลายแขวง/ตำบล" }]}
      >
        <Select
          showSearch
          placeholder="เลือกปลายแขวง/ตำบล"
          optionFilterProp="children"
          disabled={lockNonPhoneFields || !districtId}
          loading={subDistrictLoading}
          notFoundContent={subDistrictLoading ? <Spin size="small" /> : null}
          onChange={(value: number) => {
            const item = subDistricts.find((x) => x.id === value);
            if (item?.zip_code != null) {
              form.setFieldsValue({ zip_code_ship: String(item.zip_code) });
            }
          }}
        >
          {subDistricts.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name_th}
            </Option>
          ))}
        </Select>
      </FormItems>

      <FormItems
        label="รหัสไปรษณีย์ปลายทาง"
        labelAlign="left"
        name="zip_code_ship"
        rules={[{ required: true, message: "กรุณาเลือกตำบลเพื่อรับรหัสไปรษณีย์" }]}
      >
        <Input
          placeholder="รหัสไปรษณีย์ปลายทาง"
          maxLength={5}
          autoComplete="off"
          readOnly
          disabled={lockNonPhoneFields}
        />
      </FormItems>
    </FormWrapper>
  );
};

export default ParcelCreateAddressForm;

const FormItems = styled(Form.Item)`
  margin-bottom: 10px;
  width: 100%;
`;

const FormWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px 20px 2px 20px;
  background-color: #ffffffff;
`;

/** จัดวางข้อความ + ปุ่มค้นหาใต้ช่องเบอร์ (ชิดกับ wrapper ของฟอร์มแนวนอน) */
const PhoneMismatchRow = styled.div`
  margin: -4px 0 10px;
  padding-left: 33.333333%;
  padding-right: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;

  @media (max-width: 576px) {
    padding-left: 0;
  }
`;
